import React, { CSSProperties } from 'react';
import { Clock, CheckCircle, Users } from 'lucide-react';
import { formatEventTime } from '@/utils/dateUtils';
import './animations.css';

interface EventInfoOverlayProps {
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt?: string | null;
  hasImage?: boolean;
  attendeeCount?: number;
}

export const EventInfoOverlay: React.FC<EventInfoOverlayProps> = ({
  title,
  description,
  startsAt,
  endsAt,
  hasImage = true,
  attendeeCount = 0,
}) => {
  const timeDisplay = formatEventTime(startsAt, endsAt);
  const isLive = timeDisplay === 'LIVE NOW';
  const isEnded = timeDisplay === 'Ended';

  const textColor = hasImage ? '#ffffff' : '#000000';
  const subtleTextColor = hasImage ? '#ffffff' : 'rgba(0, 0, 0, 0.8)';
  const endedColor = '#9ca3af';
  const textShadow: CSSProperties = hasImage
    ? { textShadow: '0 2px 4px rgba(0, 0, 0, 0.9)' }
    : {};

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h3
          style={{ ...styles.title, color: textColor, ...textShadow }}
          title={title}>
          {title}
        </h3>
        <div style={styles.timeRow}>
          {isLive ? (
            <div style={styles.liveBadge}>
              <div style={styles.liveDot} />
              <span style={styles.liveText}>HAPPENING NOW</span>
            </div>
          ) : isEnded ? (
            <div style={styles.endedBadge}>
              <CheckCircle
                size={12}
                color="#ffffff"
                style={{ marginRight: 6 }}
              />
              <span style={styles.endedText}>EVENT ENDED</span>
            </div>
          ) : (
            <>
              <Clock
                size={14}
                color={textColor}
                style={{ marginRight: 4 }}
              />
              <span
                style={{
                  ...styles.timeText,
                  color: textColor,
                  ...textShadow,
                }}>
                {timeDisplay}
              </span>
            </>
          )}
          {!isEnded && attendeeCount > 0 && (
            <>
              <span
                style={{
                  ...styles.separator,
                  color: subtleTextColor,
                  ...textShadow,
                }}>
                ·
              </span>
              <Users
                size={14}
                color={textColor}
                style={{ marginRight: 4 }}
              />
              <span
                style={{ ...styles.attendeeText, color: textColor, ...textShadow }}>
                {attendeeCount} joined
              </span>
            </>
          )}
        </div>
        {description && (
          <p
            style={{
              ...styles.description,
              color: subtleTextColor,
              ...textShadow,
            }}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 55,
    zIndex: 1,
    pointerEvents: 'none',
  },
  content: {
    padding: '16px',
    paddingRight: '60px',
    paddingTop: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  timeRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: '4px',
    flexWrap: 'wrap',
  },
  timeText: {
    fontSize: '13px',
    fontWeight: 500,
  },
  separator: {
    fontSize: '13px',
    marginLeft: '6px',
    marginRight: '6px',
  },
  attendeeText: {
    fontSize: '13px',
    fontWeight: 500,
  },
  description: {
    fontSize: '14px',
    lineHeight: '18px',
    marginTop: '4px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  liveBadge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    borderRadius: '4px',
    padding: '3px 8px',
    marginRight: '8px',
  },
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    marginRight: '6px',
    animation: 'pulse 2s ease-in-out infinite',
  },
  liveText: {
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 'bold',
  },
  endedBadge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#6b7280',
    borderRadius: '4px',
    padding: '3px 8px',
    marginRight: '8px',
  },
  endedText: {
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 'bold',
  },
};
